// Stores
import { useSearchResultsStore } from '@white-label-store/search-results';

// Types
import type {
  ParkingFormData,
  LoungeFormData,
} from '@white-label-types/component';
import type {
  SearchProducts,
  productTypeId,
} from '@white-label-types/parking-booking';
import type { SearchCompletedData } from '@white-label-types/pusher';
import type { default as Pusher } from 'pusher-js';

// Packages
import { v4 as uuidv4 } from 'uuid';
import { createSearchCriteria } from '@white-label-helper/data-parsers-create-search-criteria';
import { buildSearchResults, buildSearchResultsForTerminalFlights } from '@white-label-helper/data-parsers-search-results';
import {
  PusherErrors,
  pusherErrorTimeout,
} from '@white-label-configuration/constants';
import getPusherChannel from '@white-label-helper/pusher-channel';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import {
  getProductSearchResults,
  searchProductAvailability,
} from '@white-label-helper/api-parkings-product';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { addCustomMetric, trackAction } from '@white-label-helper/shared-helpers';
import { TerminalFlight } from '@white-label-types/terminal-flight';
import { datadogRum } from "@datadog/browser-rum";
// Mocks
import { mockedTravelInsuranceResponse } from './mock-data/mocked-return-insurance-response';

let channel: Pusher | undefined;

type AdditionalFormData = {
  parking?: ParkingFormData;
  lounges?: LoungeFormData;
  fasttrack?: LoungeFormData;
}

export function searchPusherHelper() {
  let searchId: string;

  function destroyChannelListener() {
    if (channel === undefined) {
      channel = getPusherChannel();
    }

    channel.unsubscribe(searchId);
  }

  function fetchProductSearchResults(
    searchId: string,
    airportCode: string | undefined,
    productId: productTypeId
  ) {
    const store = useSearchResultsStore();
    const startTime = performance.now();
    const is_sync = getAppVariable('is_sync');
    return new Promise((resolve, reject) => {
      getProductSearchResults(searchId).then((response) => {
        addCustomMetric('tracking', 'Search Metric: Completed', {
          is_simple_search: String(is_sync),
          is_classic_search: String(!is_sync),
          event: 'SearchCompletedEvent',
          airport_code: airportCode,
        }, startTime, 'search_service', airportCode);

        store.updatePlaybooks(response.body?.data?.playbooks[productId]);
        if (response.body.data.search.inventory_metadata) {
          store.updateInventoryMetadata(response.body.data.search.inventory_metadata);
        }

        const results = response.body.data?.results;
        const productData = results?.[productId];
        const inventoryProvider = productData?.[0]?.inventory_provider;

        if (productId == 'fasttrack' && inventoryProvider === "copenhagen-optimization" && Array.isArray(productData)) {
          resolve(buildSearchResultsForTerminalFlights(productData));
        } else {
          // If multiple product type results exist, store them in search results store
          // TODO: remove feature flag check when feature flag 'EM-369-CROSS-SELL-MIXED-RESULTS' is no longer needed
          const multiProductTypeKeys = Object.keys(results);
          if (getFeatureFlag('EM-369-CROSS-SELL-MIXED-RESULTS') && multiProductTypeKeys.length) {
            multiProductTypeKeys.forEach((key) => {
              store.updateSearchResultsByProductType({
                searchResults: buildSearchResults(response.body.data.results[key], key as productTypeId),
                productType: key as productTypeId,
              });
            });

            if (getFeatureFlag('EM-228-TRAVEL-INSURANCE')) {
              store.updateSearchResultsByProductType({
                searchResults: buildSearchResults(mockedTravelInsuranceResponse.insurance, 'travel-insurance' as productTypeId),
                productType: 'travel-insurance' as productTypeId,
              });
            }
          }
          resolve(buildSearchResults(response.body.data?.results?.[productId], productId));
        }
    }).catch((error) => {
      console.error("Error fetching product search results:", error);
      reject(error);
    });
  });
}

  function searchProducts(
    formData: ParkingFormData | LoungeFormData,
    productId: productTypeId = 'parking',
    locale: string = '',
    additionalFormData?: AdditionalFormData,
  ) {
    const startTime = performance.now();
    const is_sync = getAppVariable('is_sync');
    const airportCode = formData.airport as string | undefined;

    return new Promise<SearchProducts[] | TerminalFlight[]>((resolve, reject) => {

      const store = useSearchResultsStore();
      searchId = uuidv4();

      let language = getAppVariable('default_language');
      if (locale) {
        language = locale;
      } else {
        language = this.$i18n.locale;
      }

      const searchCriteria = createSearchCriteria(formData, searchId, productId, language);

      // If `additionalFormData` is defined, create search criteria for each additional product type
      // TODO: remove feature flag check when feature flag 'EM-369-CROSS-SELL-MIXED-RESULTS' is no longer needed
      if (getFeatureFlag('EM-369-CROSS-SELL-MIXED-RESULTS') && additionalFormData !== undefined) {
        (Object.keys(additionalFormData) as (keyof AdditionalFormData)[]).forEach((key) => {
          if (additionalFormData[key] === undefined) return;
          searchCriteria.criteria[key] = createSearchCriteria(
            additionalFormData[key],
            searchId,
            key,
            language
          )['criteria'][key];
        });
      }

      store.updateSearchToken(searchId);

      if (channel === undefined) {
        channel = getPusherChannel();
      }

      const localPusher = channel.subscribe(`search.results.${searchId}`);

      searchProductAvailability(searchCriteria)
        .then((response) => {
          store.updatePlaybooks(response.body.data.playbooks[productId]);
          if (response.body.data.search.inventory_metadata) {
            store.updateInventoryMetadata(response.body.data.search.inventory_metadata);
          }

          if (response.body.data.booking_fee) {
            store.updateBookingFee(response.body.data.booking_fee)
          }

          if (!response.body.data.is_more_coming) {
            // If multiple product type results exist, store them in search results store
            // TODO: remove feature flag check when feature flag 'EM-369-CROSS-SELL-MIXED-RESULTS' is no longer needed
            const multiProductTypeKeys = Object.keys(response.body.data?.results);
            if (getFeatureFlag('EM-369-CROSS-SELL-MIXED-RESULTS') && multiProductTypeKeys.length) {
              multiProductTypeKeys.forEach((key) => {
                store.updateSearchResultsByProductType({
                  searchResults: buildSearchResults(
                    response.body.data.results[key],
                    key as productTypeId
                  ),
                  productType: key as productTypeId,
                })
              });
              if (getFeatureFlag('EM-228-TRAVEL-INSURANCE')) {
                store.updateSearchResultsByProductType({
                  searchResults: buildSearchResults(mockedTravelInsuranceResponse.insurance, 'travel-insurance' as productTypeId),
                  productType: 'travel-insurance' as productTypeId,
                });
              }
            }
            resolve(
              buildSearchResults(
                response.body.data.results[productId],
                productId
              )
            );
          }
        })
        .catch((error: { message: string }) => {
          datadogRum.addError(
            new Error('searchProductAvailability failed', { cause: error })
          );

          let data = null;
          try {
            data = JSON.parse(error.message);
          } catch (e) {
            console.log('possible error: ', e);
            datadogRum.addError('searchProductAvailability failed', {
              'search_service': {
                ...data,
              }
            });
          }
          reject(
            Object.assign(new Error(PusherErrors.failedOnRequest), { data })
          );
        });

      localPusher.bind(
        'SearchCompletedEvent',
        (eventData: SearchCompletedData) => {
          destroyChannelListener();
          const results = eventData?.results;
          // If results in event data, use these
          // TODO: remove feature flag check when feature flag 'EM-369-CROSS-SELL-MIXED-RESULTS' is no longer needed
          if (!getFeatureFlag('EM-369-CROSS-SELL-MIXED-RESULTS') && Array.isArray(results) && results.length) {

            addCustomMetric('tracking',
              'Search Metric: Completed', {
              is_simple_search: String(is_sync),
              is_classic_search: String(!is_sync),
              event: 'SearchCompletedEvent',
            }, startTime, 'search_service', airportCode)

            if (eventData.playbooks) {
              store.updatePlaybooks(eventData.playbooks[productId]);
            }
            if (eventData.inventory_metadata) {
              store.updateInventoryMetadata(eventData.inventory_metadata);
            }
            resolve(buildSearchResults(results, productId));
            return;
          }

          // request API for results
          resolve(fetchProductSearchResults(searchId, airportCode, productId));
        }
      );

      localPusher.bind('SearchProductFailedEvent', (error: { product_code: string; error: { response: string } }) => {
        // log failed search action with datadog
        trackAction('SearchProductFailedEvent', {
          productId,
          searchId,
        });
        if (!error.product_code || error.product_code === getAppVariable('primary_product_type')) {
          reject(new Error('SearchProductFailedEvent'));
        } else {
          addCustomMetric("error", "Search Metric: Secondary product error", {
            is_simple_search: String(is_sync),
            is_classic_search: String(!is_sync),
            failure_response: error.error?.response || "An error occurred",
          }, startTime, "search_service", airportCode);
        }
      });

      // Default timeout to reject request if it's frozen
      setTimeout(() => {
        reject(new Error(PusherErrors.failedOnPusherTimeout));
      }, pusherErrorTimeout);
    }).catch((e) => {
      addCustomMetric("error", "Search Metric: Exception", {
        is_simple_search: String(is_sync),
        is_classic_search: String(!is_sync),
        failure_response: e.message,
      }, startTime, "search_service", airportCode);

      throw e;
    })
  }

  function clearChannel() {
    channel = undefined;
  }

  return {
    searchProducts,
    destroyChannelListener,
    clearChannel
  };
}
